import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { useGetPageRoutingURL } from 'js/hooks/useGetPageRoutingURL';
import { htcmdService } from 'js/services/htcmd.service';
import {
    isHashLink,
    getPartsUrl,
    getCleanUrl,
    isExternalUrl,
    getCombinedSearch,
    isDocumentsAndMediaUrl,
    getPathWithTrailingSlash
} from 'js/utils/url.util';

import style from './Link.scss';

export const Link = ({ children, href = '', className = '', isDefaultLinkClass = true, inNewWindow = false, ...other }) => {
    const location = useLocation();
    const { useTrailingSlash } = useAppSelector(prop('seo'));
    const attribs = {
        href,
        className: `${isDefaultLinkClass ? style.link : ''} ${className}`,
        target: inNewWindow ? '_blank' : '_self',
        ...other
    };
    const linkPath = useGetPageRoutingURL(attribs.href);
    const isDocAndMediaUrl = isDocumentsAndMediaUrl(href);

    if (isDocAndMediaUrl) {
        const [, , , formattedPath] = getPartsUrl(href);

        attribs.href = getCleanUrl(formattedPath);
    }

    const isHashLinkUrl = isHashLink(linkPath);
    const isExternalLinkUrl = isExternalUrl(linkPath);
    const {
        isWrapperEnabled,
        isEnabled: isNativeEnabled
    } = htcmdService;

    if (isExternalLinkUrl || isHashLinkUrl || isDocAndMediaUrl) {
        if (isWrapperEnabled && (isExternalLinkUrl || isDocAndMediaUrl)) {
            // in case we are in the wrapper, ensure that for all external links and library links
            // we use the htcmd command to navigate
            attribs.href = htcmdService.prepareCommand('navigate', {
                type: isExternalLinkUrl ? 'external' : 'relative',
                path: attribs.href
            });

            delete attribs.target;
        }

        return <a {...attribs}>{children}</a>;
    }

    if ((isWrapperEnabled || isNativeEnabled) && attribs['data-native-href']) {
        // in case the link has data-native-href="" and we are in the wrapper or in the mobile app
        // then use this attributes value in the link instead of the original href
        attribs.href = attribs['data-native-href'];

        delete attribs.target;

        return <a {...attribs}>{children}</a>;
    }

    const link = new window.URL(linkPath, document.baseURI);
    const to = {
        state: {
            prevPath: location.pathname
        },
        pathname: getPathWithTrailingSlash(link.pathname, useTrailingSlash),
        search: getCombinedSearch(link.search, location.search),
        hash: link.hash
    };

    return <RouterLink to={to} {...attribs}>{children}</RouterLink>;
};
